<template>
  <div class="armory-panel">
    <div v-if="loading" class="armory-panel-state">
      <i class="fa-solid fa-circle-notch fa-spin"></i>
      <span>Loading {{ sectionLabel }} from the armory...</span>
    </div>

    <div v-else-if="error" class="armory-panel-state is-error">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <div>
        <strong>{{ error }}</strong>
        <small>The armory page is the only source for this data and it is not always available.</small>
      </div>
      <div class="armory-panel-state-actions">
        <button class="btn-destructive" type="button" @click="load(true)">
          <i class="fa-solid fa-rotate"></i> Retry
        </button>
        <a class="btn-destructive" :href="armoryUrl" target="_blank" rel="noreferrer">
          <i class="fa-solid fa-arrow-up-right-from-square"></i> Open armory
        </a>
      </div>
    </div>

    <template v-else-if="section === 'profile' && payload">
      <section v-if="payload.stats" class="armory-block">
        <h3>Character stats</h3>
        <div class="armory-stat-groups">
          <div v-for="group in overviewStatGroups" :key="group.name" class="armory-stat-group">
            <h4>{{ group.name }}</h4>
            <ul class="armory-stat-grid">
              <li v-for="stat in group.stats" :key="stat.slug" :class="`is-${stat.slug}`">
                <span class="armory-stat-icon"><i :class="statIcon(stat.slug)"></i></span>
                <span class="armory-stat-copy">
                  <strong>{{ statValue(stat) }}</strong>
                  <small>{{ stat.label }}</small>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section v-if="detailGroups.length" class="armory-block">
        <h3>Detailed stats</h3>
        <div class="armory-detail-grid">
          <div v-for="group in detailGroups" :key="group.name" class="armory-detail-group">
            <h4>{{ group.name }}</h4>
            <dl>
              <div v-for="stat in group.stats" :key="`${group.name}-${stat.slug}`">
                <dt>{{ stat.label }}</dt>
                <dd>{{ statValue(stat) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section v-if="payload.pvp" class="armory-block">
        <h3>Player vs player</h3>
        <div class="armory-pvp-head">
          <div>
            <strong>{{ formatNumber(payload.pvp.honorLevel) }}</strong>
            <small>Honor level</small>
          </div>
          <div>
            <strong>{{ formatNumber(payload.pvp.honorableKills) }}</strong>
            <small>Honorable kills</small>
          </div>
        </div>
        <ul class="armory-pvp-grid">
          <li v-for="bracket in payload.pvp.brackets" :key="bracket.key" :class="{ 'is-unrated': !bracket.rating }">
            <img v-if="bracket.tierIcon" :src="bracket.tierIcon" :alt="bracket.label" loading="lazy">
            <strong>{{ bracket.rating || 'Unranked' }}</strong>
            <span>{{ bracket.label }}</span>
            <small>{{ bracket.kind }}</small>
            <em v-if="bracket.total">{{ bracket.wins }}W / {{ bracket.losses }}L</em>
          </li>
        </ul>
      </section>

      <section v-if="payload.raids && payload.raids.raids.length" class="armory-block">
        <h3>Raid progression <small>{{ payload.raids.expansion }}</small></h3>
        <div class="armory-raid-grid">
          <article v-for="raid in payload.raids.raids" :key="raid.id" class="armory-raid-card">
            <header>
              <img v-if="raid.iconUrl" :src="raid.iconUrl" :alt="raid.name" loading="lazy">
              <div>
                <strong>{{ raid.name }}</strong>
                <small v-if="raid.level">Level {{ raid.level }}</small>
              </div>
            </header>
            <ul>
              <li v-for="difficulty in raid.difficulties" :key="`${raid.id}-${difficulty.id}`">
                <span class="armory-raid-difficulty">{{ difficulty.name }}</span>
                <span class="armory-progress" :title="difficultyBossTitle(difficulty)">
                  <span
                    class="armory-progress-fill"
                    :class="progressClass(difficulty)"
                    :style="{ width: `${percentOf(difficulty.killed, difficulty.total)}%` }"
                  ></span>
                  <b>{{ difficulty.killed }}/{{ difficulty.total }}</b>
                </span>
              </li>
            </ul>
          </article>
        </div>
      </section>
    </template>

    <template v-else-if="section === 'reputation' && payload">
      <section v-for="group in payload.groups" :key="group.id" class="armory-block">
        <h3>{{ group.name }}</h3>
        <ul class="armory-reputation-list">
          <template v-for="faction in group.factions" :key="faction.id">
            <li>
              <span class="armory-reputation-name">{{ faction.name }}</span>
              <span class="armory-progress">
                <span
                  class="armory-progress-fill"
                  :class="standingClass(faction)"
                  :style="{ width: `${percentOf(faction.value, faction.maxValue)}%` }"
                ></span>
                <b v-if="faction.maxValue">{{ formatNumber(faction.value) }} / {{ formatNumber(faction.maxValue) }}</b>
              </span>
              <span class="armory-reputation-standing" :class="standingClass(faction)">{{ faction.standing }}</span>
            </li>
            <li v-for="child in faction.children" :key="child.id" class="is-nested">
              <span class="armory-reputation-name">{{ child.name }}</span>
              <span class="armory-progress">
                <span
                  class="armory-progress-fill"
                  :class="standingClass(child)"
                  :style="{ width: `${percentOf(child.value, child.maxValue)}%` }"
                ></span>
                <b v-if="child.maxValue">{{ formatNumber(child.value) }} / {{ formatNumber(child.maxValue) }}</b>
              </span>
              <span class="armory-reputation-standing" :class="standingClass(child)">{{ child.standing }}</span>
            </li>
          </template>
        </ul>
      </section>
    </template>

    <template v-else-if="section === 'achievements' && payload">
      <section class="armory-block">
        <h3>Achievements <small>{{ formatNumber(payload.totalPoints) }} points earned</small></h3>
        <div class="armory-achievement-chart">
          <div
            v-for="category in payload.categories"
            :key="category.slug"
            class="armory-achievement-row"
            :style="{ '--armory-share': `${category.percent === null ? 100 : category.percent}%` }"
          >
            <div class="armory-achievement-main">
              <strong>{{ category.name }}</strong>
              <span v-if="category.total">{{ formatNumber(category.count) }} / {{ formatNumber(category.total) }} · {{ category.percent }}%</span>
              <span v-else>{{ formatNumber(category.count) }} earned</span>
            </div>
            <div class="armory-achievement-track">
              <span class="armory-achievement-bar" :class="{ 'is-complete': category.percent === 100 || category.percent === null }"></span>
            </div>
            <!-- Rendered even when empty so every track in the grid starts and ends on the same
                 x position; Feats of Strength and Legacy report no points. -->
            <span class="armory-achievement-points">
              <template v-if="category.points">
                <b><i class="fa-solid fa-shield"></i> {{ formatNumber(category.points) }}</b>
                <small>of {{ formatNumber(category.totalPoints) }}</small>
              </template>
            </span>
          </div>
        </div>
      </section>
    </template>

  </div>
</template>

<script>
import { getArmorySection } from '../services/armoryProfile'
import { encodedCharacterPath } from '../utils/characterSlug'

const SECTION_LABELS = {
  profile: 'character stats',
  reputation: 'reputations',
  achievements: 'achievements',
}
// Everything else in the overview list (health, resource, primary attribute, stamina) belongs to
// the first group.
const SECONDARY_STAT_SLUGS = ['critical-strike', 'haste', 'mastery', 'versatility']
const STAT_ICONS = {
  agility: 'fa-solid fa-feather',
  armor: 'fa-solid fa-shield',
  'attack-dps': 'fa-solid fa-burst',
  'attack-power': 'fa-solid fa-hand-fist',
  'attack-speed': 'fa-solid fa-stopwatch',
  avoidance: 'fa-solid fa-person-walking-arrow-right',
  block: 'fa-solid fa-shield-halved',
  'bonus-armor': 'fa-solid fa-shield-heart',
  'critical-strike': 'fa-solid fa-crosshairs',
  damage: 'fa-solid fa-khanda',
  dodge: 'fa-solid fa-arrows-left-right',
  energy: 'fa-solid fa-bolt',
  focus: 'fa-solid fa-bullseye',
  fury: 'fa-solid fa-fire',
  haste: 'fa-solid fa-clock',
  health: 'fa-solid fa-heart',
  intellect: 'fa-solid fa-brain',
  'item-level': 'fa-solid fa-layer-group',
  leech: 'fa-solid fa-droplet',
  mana: 'fa-solid fa-star',
  'mana-regen': 'fa-solid fa-rotate',
  mastery: 'fa-solid fa-crown',
  'movement-speed': 'fa-solid fa-person-running',
  pain: 'fa-solid fa-fire',
  parry: 'fa-solid fa-hand',
  rage: 'fa-solid fa-face-angry',
  'runic-power': 'fa-solid fa-snowflake',
  'spell-power': 'fa-solid fa-wand-sparkles',
  stamina: 'fa-solid fa-plus',
  strength: 'fa-solid fa-dumbbell',
  versatility: 'fa-solid fa-screwdriver-wrench',
}
// Percent-based standings (Renown, follower levels) read as a track, plain reputation tiers as a
// classic bar; they get different colours so the two are not confused at a glance.
const RENOWN_STANDING_PATTERN = /^(?:renown|level)\b/i

export default {
  name: 'ArmoryProfilePanel',
  props: {
    profile: {
      type: Object,
      required: true,
    },
    section: {
      type: String,
      default: 'profile',
    },
  },
  emits: ['loaded'],
  data() {
    return {
      payload: null,
      loading: false,
      error: '',
    }
  },
  computed: {
    sectionLabel() {
      return SECTION_LABELS[this.section] || 'data'
    },
    overviewStatGroups() {
      const overview = this.payload?.stats?.overview || []
      const secondary = overview.filter((stat) => SECONDARY_STAT_SLUGS.includes(stat.slug))
      const primary = overview.filter((stat) => !SECONDARY_STAT_SLUGS.includes(stat.slug))

      return [
        { name: 'Primary', stats: primary },
        { name: 'Secondary', stats: secondary },
      ].filter((group) => group.stats.length)
    },
    detailGroups() {
      return (this.payload?.stats?.groups || []).filter((group) => group.stats.length)
    },
    armoryUrl() {
      const path = encodedCharacterPath(this.profile)
      const locale = path.region === 'eu' ? 'en-gb' : 'en-us'
      return `https://worldofwarcraft.blizzard.com/${locale}/character/${path.region}/${path.realm}/${path.name}`
    },
  },
  watch: {
    section() {
      this.load()
    },
  },
  mounted() {
    // Only the section on screen is fetched: the profile page alone is ~1.4 MB upstream.
    this.load()
  },
  methods: {
    async load(force = false) {
      if (this.loading) return

      this.loading = true
      this.error = ''

      try {
        this.payload = await getArmorySection(this.section, this.profile, { force })
        // The dialog owns the shared "armory data from …" footer, so it needs the timestamp of
        // whichever section was loaded last.
        this.$emit('loaded', this.payload)
      } catch (error) {
        this.payload = null
        this.error = error?.status === 404
          ? 'This character was not found on the armory.'
          : 'Unable to load this section from the armory.'
      } finally {
        this.loading = false
      }
    },
    statIcon(slug) {
      return STAT_ICONS[slug] || 'fa-solid fa-circle-dot'
    },
    statValue(stat) {
      if (!stat) return '-'
      if (stat.valueType === 'RANGE') return `${this.formatDecimal(stat.min)} - ${this.formatDecimal(stat.max)}`
      if (stat.valueType === 'PERCENTAGE') return `${Math.round(stat.value || 0)}%`
      if (stat.valueType === 'WHOLE') return this.formatNumber(Math.round(stat.value || 0))
      // The armory omits the value for stats a spec cannot have (leech on most classes) and
      // renders them as zero rather than as a gap.
      return this.formatDecimal(stat.value ?? 0)
    },
    formatDecimal(value) {
      if (value === null || value === undefined) return '-'
      if (Number.isInteger(value)) return this.formatNumber(value)
      return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatNumber(value) {
      if (value === null || value === undefined) return '-'
      return Number(value).toLocaleString('en-US')
    },
    formatTimestamp(value) {
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return 'an unknown time'
      return date.toLocaleString()
    },
    percentOf(value, total) {
      if (!total) return 0
      return Math.min(100, Math.round(((value || 0) / total) * 100))
    },
    progressClass(difficulty) {
      if (!difficulty.total) return ''
      if (difficulty.killed >= difficulty.total) return 'is-complete'
      return difficulty.killed ? 'is-partial' : 'is-empty'
    },
    standingClass(faction) {
      if (RENOWN_STANDING_PATTERN.test(faction.standing || '')) return 'is-renown'
      return faction.max ? 'is-complete' : 'is-standing'
    },
    difficultyBossTitle(difficulty) {
      return difficulty.bosses
        .map((boss) => `${boss.name}: ${boss.killCount ? `${boss.killCount} kills` : 'not killed'}`)
        .join('\n')
    },
  },
}
</script>
